// Review Center -> Shop drawings (owner, 2026-10-05: "let the approval also
// show in the review center, not only in the shop drawings").
//
// THE BADGE WAS ALREADY THERE AND THE TAB WAS NOT. routes/reviewCenter has
// counted drawings at `under_review` since the lane was built, and
// 'shopDrawings' is in its TABS list - so the number was computed, reported
// and never shown. A count on a tab nobody can open is worse than no count:
// it says somebody is waiting and gives you nowhere to go.
//
// APPROVAL GOES THROUGH THE SAME ROUTE AS THE SHOP DRAWINGS TAB, deliberately
// and without exception. POST /:id/decide is where the schedule's sign-off
// gate runs, where the announcement to the recipients is recorded, and where
// a double-click is made idempotent. A second approval path would be a second
// answer to "is this signed off?", and two answers to that question is the
// family of bug this codebase has spent weeks removing.
//
// WHAT THIS TAB ADDS over the register: it says what the approval DID about
// the deliverable. The mail is not sent by the approval - it is recorded as
// owed and drained by the hourly sender - so "approved" alone leaves the
// reviewer to guess whether anyone is being told.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Empty, Input, Modal, Space, Table, Tag, Tooltip, message } from "antd";
import { CheckOutlined, CloseOutlined, FileTextOutlined } from "@ant-design/icons";
import { url } from "api";
import { getShopDrawings, decideShopDrawing } from "api/tracking/shopDrawingsApi";
import { getDocVersions } from "api/docControl/getDocVersions";
import ActivityPathLine from "pages/ShopDrawings/ActivityPathLine";

const T = { muted: "#667085", line: "#EFEFEF" };
const dt = (v) => (v ? String(new Date(v).toISOString()).slice(0, 10) : "");

export default function ShopDrawingsTab({ projectId, filter, reloadKey, onCountChange, onDecided }) {
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(false);
  const [picked, setPicked] = useState([]);
  const [working, setWorking] = useState("");
  const [opening, setOpening] = useState("");
  const [sendBack, setSendBack] = useState(null);     // { rows, note }

  const load = useCallback(async () => {
    if (!projectId) { setRows([]); return; }
    setBusy(true);
    try {
      // WAITING FOR APPROVAL IS THE QUEUE, and it is asked for by name
      // rather than filtered in the browser: the register is capped at
      // 2,000 rows, so a client-side filter would be a filter over
      // whichever page happened to arrive.
      const r = await getShopDrawings(projectId, { filters: 1, status: "under_review" });
      const list = r.drawings || [];
      setRows(list);
      setPicked((prev) => prev.filter((id) => list.some((x) => String(x._id) === String(id))));
      if (onCountChange) onCountChange(list.length);
    } catch (e) {
      message.error(e.message || "Could not read the shop drawings waiting for approval");
      setRows([]);
    } finally {
      setBusy(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, reloadKey]);
  useEffect(() => { load(); }, [load]);

  // The page's search box, applied to what a person can see on the row.
  // The status is already the query, so this narrows rather than selects.
  const shown = useMemo(() => {
    const q = String((filter && filter.q) || "").trim().toLowerCase();
    if (!q) return rows || [];
    return (rows || []).filter((r) => [r.name, r.sheetNumber, r.area, r.package, r.system,
      ...(r.responsible || []).map((x) => x && (x.name || x.email))]
      .filter(Boolean).some((v) => String(v).toLowerCase().includes(q)));
  }, [rows, filter]);

  // The drawing itself, latest revision, through the route that signs its
  // own urls. A reviewer approving a drawing they have not opened is the
  // thing this tab exists to make less likely.
  const openFile = useCallback(async (row) => {
    if (!row.docControlId) { message.info("Nothing is filed against this drawing yet."); return; }
    setOpening(String(row._id));
    try {
      const data = await getDocVersions(String(row.docControlId));
      const list = (data && data.versions) || [];
      if (!list.length) { message.info("Nothing is filed against this drawing yet."); return; }
      const newest = list.reduce((hi, v) => (Number(v.rev) > Number(hi.rev) ? v : hi), list[0]);
      if (!newest.url) { message.warning(`Revision ${newest.rev} has no file on this server.`); return; }
      const w = window.open(`${url}${newest.url}`, "_blank", "noopener");
      if (!w) message.warning("Your browser blocked the new tab - allow popups for this site.");
    } catch (e) {
      message.error(e.message || "Could not open that drawing");
    } finally {
      setOpening("");
    }
  }, []);

  // WHAT THE APPROVAL DID ABOUT THE DELIVERABLE, said in the toast.
  //
  // The decide route answers with `announcement: { queued, reason }`
  // because approval does NOT send the mail - it records that one is owed
  // and the hourly sender carries it, with the file attached while the
  // 10 MB budget holds and a signed link beyond it. So the reviewer is
  // told which of the two happened rather than inferring it from silence.
  const say = (r, approve) => {
    if (!approve) {
      message.success("Returned to the responsible - the due date has not changed");
      return;
    }
    const where = (r.drawing && r.drawing.scheduleName) || "the activity";
    const ann = r.announcement || {};
    message.success(ann.queued
      ? `Approved - "${where}" is signed off, and the deliverable goes to the recipients on the next send`
      : `Approved - "${where}" is signed off. Nobody is being sent it${ann.reason ? `: ${ann.reason}` : ""}`);
  };

  const decide = useCallback(async (list, approve, note = "") => {
    setWorking(approve ? "approve" : "reject");
    let ok = 0;
    const refused = [];
    for (const row of list) {
      try {
        // eslint-disable-next-line no-await-in-loop
        const r = await decideShopDrawing(projectId, row._id, { approve, note });
        ok += 1;
        if (list.length === 1) say(r, approve);
      } catch (e) {
        // THE SCHEDULE'S GATE REFUSED, IN ITS OWN WORDS - an open issue on
        // the activity, or a child not signed off. Named per drawing: in a
        // bulk approval the reviewer needs to know WHICH one and why, and
        // "3 of 5 approved" on its own is a number to go hunting with.
        refused.push({ name: row.name, why: e.message || "refused" });
      }
    }
    setWorking("");
    setPicked([]);
    if (list.length > 1) {
      message.success(`${ok} of ${list.length} ${approve ? "approved" : "returned"}`);
    }
    if (refused.length) {
      Modal.info({
        title: `${refused.length} ${refused.length === 1 ? "drawing was" : "drawings were"} not ${approve ? "approved" : "returned"}`,
        content: (
          <div style={{ maxHeight: 260, overflow: "auto" }}>
            {refused.map((x) => (
              <div key={x.name} style={{ marginBottom: 6 }}>
                <b>{x.name}</b><br /><span style={{ color: T.muted }}>{x.why}</span>
              </div>
            ))}
          </div>
        ),
        getContainer: () => document.body,
      });
    }
    await load();
    if (onDecided) onDecided();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, load, onDecided]);

  const columns = [
    {
      title: "Drawing",
      dataIndex: "name",
      render: (v, r) => (
        <span>
          {v}
          <div style={{ fontSize: 11, color: T.muted }}>
            {[r.sheetNumber, r.area, r.package, r.system].filter(Boolean).join(" · ") || "no area or package"}
          </div>
          {/* The activity approving this signs off, as its full path. */}
          <ActivityPathLine activity={{ activityFullPath: r.activityFullPath, name: r.scheduleName }}
                            missing={!!r.scheduleMissing} style={{ fontSize: 11 }} />
        </span>
      ),
    },
    {
      title: "Delivered",
      dataIndex: "submittedAt",
      width: 170,
      render: (v, r) => (
        <span>
          {dt(v) || "-"}
          <div style={{ fontSize: 11, color: T.muted }}>
            {(r.submittedBy && (r.submittedBy.name || r.submittedBy.email)) || "-"}
          </div>
        </span>
      ),
    },
    {
      title: "Rev",
      dataIndex: "revision",
      width: 70,
      render: (v, r) => (
        <span>
          r{Number(v || 0)}
          {/* A RE-DELIVERY AFTER A RE-ISSUE IS NOT AN ORDINARY ONE, and the
              reviewer is the person who asked for it. The closed request
              is on the row rather than in a history nobody opens. */}
          {r.reissueCount ? (
            <Tooltip title={`${r.reissueCount} re-issue ${r.reissueCount === 1 ? "request" : "requests"} on this drawing`}>
              <Tag color="orange" style={{ marginLeft: 6, marginInlineEnd: 0 }}>re-issued</Tag>
            </Tooltip>
          ) : null}
        </span>
      ),
    },
    { title: "Hours", dataIndex: "hours", width: 80, render: (v) => (v === null || v === undefined ? "-" : v) },
    {
      title: "Responsible",
      width: 200,
      render: (_, r) => ((r.responsible || []).map((x) => x && (x.name || x.email)).filter(Boolean).join(", ") || "-"),
    },
    {
      title: "",
      key: "actions",
      width: 220,
      render: (_, r) => (
        <Space size={4}>
          <Tooltip title="Open the drawing - the revision that was delivered">
            <Button size="small" icon={<FileTextOutlined />} loading={String(opening) === String(r._id)}
                    onClick={() => openFile(r)} />
          </Tooltip>
          <Button size="small" type="primary" icon={<CheckOutlined />}
                  loading={working === "approve"}
                  onClick={() => decide([r], true)}>
            Approve
          </Button>
          <Button size="small" danger icon={<CloseOutlined />}
                  onClick={() => setSendBack({ rows: [r], note: "" })}>
            Send back
          </Button>
        </Space>
      ),
    },
  ];

  if (rows && !rows.length) {
    return <Empty description="No shop drawing is waiting for approval" />;
  }

  return (
    <div>
      {/* APPROVING IS THE SCHEDULE'S SIGN-OFF, and this is the one place a
          reviewer might not know that - on the register it is next to the
          activity it signs off. */}
      <div style={{ color: T.muted, fontSize: 12.5, marginBottom: 8 }}>
        Approving a drawing signs off the schedule activity it belongs to, and the deliverable is then sent to its
        recipients on the next send - attached, or as a link when it is too large to attach.
      </div>
      {picked.length ? (
        <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 10px", marginBottom: 8,
                      border: `1px solid ${T.line}`, borderRadius: 6, background: "#fafafa" }}>
          <b>{picked.length}</b>
          <span>{picked.length === 1 ? "drawing selected" : "drawings selected"}</span>
          <Button size="small" type="primary" icon={<CheckOutlined />} loading={working === "approve"}
                  onClick={() => decide((rows || []).filter((r) => picked.includes(String(r._id))), true)}>
            Approve selected
          </Button>
          <Button size="small" danger icon={<CloseOutlined />}
                  onClick={() => setSendBack({ rows: (rows || []).filter((r) => picked.includes(String(r._id))), note: "" })}>
            Send back selected
          </Button>
          <Button size="small" onClick={() => setPicked([])}>Clear</Button>
        </div>
      ) : null}
      <Table
        rowKey="_id"
        size="small"
        loading={busy}
        dataSource={shown}
        columns={columns}
        pagination={{ pageSize: 25, size: "small", hideOnSinglePage: true }}
        rowSelection={{ selectedRowKeys: picked, onChange: (k) => setPicked(k.map(String)) }}
      />

      {/* SEND BACK CARRIES A NOTE, because being returned without one is a
          question the responsible has to ask by reply. One note for the
          whole pick, as the other tabs do it. */}
      <Modal
        title={sendBack
          ? (sendBack.rows.length === 1
            ? `Send "${sendBack.rows[0].name}" back`
            : `Send ${sendBack.rows.length} drawings back`)
          : ""}
        open={!!sendBack}
        onCancel={() => setSendBack(null)}
        onOk={() => { const s = sendBack; setSendBack(null); decide(s.rows, false, String(s.note || "").trim()); }}
        okText="Send back"
        okButtonProps={{ danger: true }}
        confirmLoading={working === "reject"}
        destroyOnClose
      >
        <p style={{ color: T.muted, marginTop: 0 }}>
          The due date does not change. The responsible is told, and uploads a new revision when it is ready.
        </p>
        <Input.TextArea
          rows={3}
          placeholder="What has to change? (optional)"
          value={sendBack ? sendBack.note : ""}
          onChange={(e) => setSendBack((prev) => (prev ? { ...prev, note: e.target.value } : prev))}
        />
      </Modal>
    </div>
  );
}
